import { PostOrPage } from "@tryghost/content-api";
import { format } from "date-fns";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { BlogPostAuthor } from "../BlogPostAuthor";
import { PostPreviewImage } from "../BlogPostPreview";
import { V3Body } from "../Typography";
import { VerticalSpacing } from "../VerticalSpacing";
import {
    BlogArticleReadingTime,
    BlogHeroComponentTag,
    BlogHeroComponentWrapperCard,
    BlogHeroExcerpt,
    BlogHeroLongTitle,
    BlogHeroShortTitle,
    BlogHeroTagReadingTime,
    BlogHeroVideoIframe,
    BlogHeroYTHPIframe
} from "./BlogHeroComponent.styles";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { PPImage } from "../BlogPostPreview/BlogPostPreview.styles";
import { OptionalNextLink } from "../ReusableStyledComponents/OptionalNextLink";
import { UnstyledInlineLink } from "../ReusableStyledComponents/UnstyledLink";

interface BlogHeroComponentProps {
    post: PostOrPage;
    postPage?: boolean;
    hasTutorialTag?: boolean;
}

const AUTHORS = {
    eric: {
        name: "Eric Hauch",
        slug: "eric"
    },
    emanuel: {
        name: "Emanuel Jöbstl",
        slug: "emanuel"
    },
    protim: {
        name: "Protim Bhaumik",
        slug: "protim"
    },
    shreya: {
        name: "Shreya Bose",
        slug: "shreya"
    }
};

const getEditedBy = (author: string) => {
    switch (author) {
        case "protim":
            return AUTHORS.shreya;
        default:
            return AUTHORS.protim;
    }
};

const getReviewedBy = (author: string) => {
    switch (author) {
        case "eric":
            return AUTHORS.shreya;
        default:
            return AUTHORS.eric;
    }
};

export const BlogHeroComponent = ({ post, postPage = false, hasTutorialTag = false }: BlogHeroComponentProps) => (
    <>
        <BlogHeroComponentWrapperCard postPage={postPage}>
            <div>
                <VerticalSpacing bottom={18}>
                    <BlogHeroTagReadingTime>
                        <BlogHeroComponentTag>
                            <OptionalNextLink
                                href={!hasTutorialTag ? `${APPLICATION_ROUTES.BLOG_TAG}/${post?.primary_tag?.slug}` : undefined}
                                useNextRouter={false}
                            >
                                <V3Body>
                                    <strong>{!hasTutorialTag ? post?.primary_tag?.name : "Tutorial"}</strong>
                                </V3Body>
                            </OptionalNextLink>
                        </BlogHeroComponentTag>
                        {postPage && <BlogArticleReadingTime>{post.reading_time} mins read</BlogArticleReadingTime>}
                    </BlogHeroTagReadingTime>
                </VerticalSpacing>
                <VerticalSpacing bottom={18}>
                    <OptionalNextLink href={!postPage ? `${APPLICATION_ROUTES.BLOG}/${post.slug}` : undefined} useNextRouter={false}>
                        {!!post.title && post?.title.length <= 48 ? (
                            <BlogHeroShortTitle>{post.title}</BlogHeroShortTitle>
                        ) : (
                            <BlogHeroLongTitle>{post.title}</BlogHeroLongTitle>
                        )}
                    </OptionalNextLink>
                </VerticalSpacing>
                <VerticalSpacing bottom={24}>
                    <OptionalNextLink href={!postPage ? `${APPLICATION_ROUTES.BLOG}/${post.slug}` : undefined} useNextRouter={false}>
                        <BlogHeroExcerpt $numLines={postPage ? 20 : 3}>{post.excerpt}</BlogHeroExcerpt>
                    </OptionalNextLink>
                </VerticalSpacing>
                <BlogPostAuthor post={post} postPage={postPage} />
                {postPage && !hasTutorialTag && (
                    <>
                        <VerticalSpacing top={18}>
                            <V3BodyGrey48 suppressHydrationWarning>
                                Written by{" "}
                                <UnstyledInlineLink href={`${APPLICATION_ROUTES.BLOG_AUTHOR}/${post.primary_author?.slug}`} target="_blank">
                                    {post.primary_author?.name}
                                </UnstyledInlineLink>
                                , edited by{" "}
                                <UnstyledInlineLink
                                    href={`${APPLICATION_ROUTES.BLOG_AUTHOR}/${getEditedBy(post.primary_author?.slug || "").slug}`}
                                    target="_blank"
                                >
                                    {getEditedBy(post.primary_author?.slug || "").name}
                                </UnstyledInlineLink>
                                , reviewed by{" "}
                                <UnstyledInlineLink
                                    href={`${APPLICATION_ROUTES.BLOG_AUTHOR}/${getReviewedBy(post.primary_author?.slug || "").slug}`}
                                    target="_blank"
                                >
                                    {getReviewedBy(post.primary_author?.slug || "").name}
                                </UnstyledInlineLink>
                                .
                            </V3BodyGrey48>
                        </VerticalSpacing>
                        <VerticalSpacing top={8}>
                            <V3BodyGrey48 suppressHydrationWarning>
                                {`${format(new Date(post.published_at || ""), "d. MMM  yyyy")}`}
                                {postPage && post.updated_at ? `, updated ${format(new Date(post.updated_at), "d. MMM  yyyy")}` : ""}
                            </V3BodyGrey48>
                        </VerticalSpacing>
                    </>
                )}
            </div>
            <div>
                <OptionalNextLink
                    href={!postPage ? `${APPLICATION_ROUTES.BLOG}/${post.slug}` : undefined}
                    useNextRouter={false}
                    ariaLabel={`Read more about ${post.title || ""}`}
                >
                    {!post.codeinjection_foot?.length ? (
                        <PostPreviewImage>
                            <PPImage
                                src={post.feature_image || ""}
                                loading="lazy"
                                alt={`Preview image of ${post.title}`}
                                srcSet={`${post.feature_image || ""} 750w, ${post.feature_image || ""} 1050w,  ${post.feature_image || ""} 1750w`}
                                sizes={`(max-width: 48rem) 750w, (max-width: 64rem) 1050w, 1750w`}
                            />
                        </PostPreviewImage>
                    ) : (
                        <BlogHeroVideoIframe>
                            <BlogHeroYTHPIframe
                                src={`https://www.youtube.com/embed/${post.codeinjection_foot}?cc_load_policy=1`}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                frameBorder="0"
                            />
                        </BlogHeroVideoIframe>
                    )}
                </OptionalNextLink>
            </div>
        </BlogHeroComponentWrapperCard>
    </>
);
